const plugin = require('tailwindcss/plugin');

const round = (num) =>
  num
    .toFixed(7)
    .replace(/(\.[0-9]+?)0+$/, '$1')
    .replace(/\.0$/, '');
const rem = (px) => `${round(px / 16)}rem`;

module.exports = {
  mode: 'jit', // https://tailwindcss.com/docs/just-in-time-mode
  content: ['./src/**/*.{js,jsx,ts,tsx}'],
  theme: {
    screens: {
      xs: '375px',
      sm: '450px',
      md: '768px',
      lg: '1024px',
      lg2: '1280px',
      xl: '1440px',
      mobile: { max: '767px' },
      tablet: { min: '768px', max: '1023px' },
      'mobile+tablet': { max: '1023px' },
    },
    container: {
      center: true,
      padding: {
        DEFAULT: '16px',
        md: '32px',
        lg: '64px',
        xl: '120px',
      },
    },
    colors: {
      transparent: 'transparent',
      current: 'currentColor',
      black: '#000000',
      white: '#FFFFFF',
      grey: {
        overlay: '#292525',
        light: '#F9FAFC', // light grey
        mid: '#DEE3E9', // mid grey
        'font-1': '#413E3E', // font 1
        'font-2': '#5A5353', // font 2
        disabled: '#ADA9A9', // disabled grey
        testimonial: '#6C6C6C',
      },
      blue: {
        dark: '#000F57', // dark blue (new homepage gradient)
        navy: '#16215A', // navy blue
        bold: '#2964F6', // bold blue
        ocean: '#041490', // rev ocean
        'bold-hover': '#1B55E2', // bold blue hover
        'bold-active': '#194DCD', // bold blue active
      },
      red: {
        error: '#E20000', // error
      },
      inherit: 'inherit',
    },
    fontFamily: {
      // 'helvetica-neue': ['Helvetica Neue', 'sans-serif'],
      'arial-nova': [
        'Arial Nova',
        'Arial',
        'Helvetica Neue',
        'Helvetica',
        'sans-serif',
      ],
      // roboto: ['Roboto', 'sans-serif'],
    },
    fontSize: {
      none: rem(0),
      base: rem(16),
      hero: [
        rem(56),
        {
          lineHeight: rem(75),
          letterSpacing: '7px',
        },
      ],
      'hero-m': [
        rem(36),
        {
          lineHeight: rem(36.5),
          letterSpacing: '3px',
        },
      ],
      h1: [rem(68), '1.25'],
      'h1-m': [rem(40), '1.25'],
      h2: [rem(50), '1.3'],
      'h2-m': [rem(30), '1.25'],
      'h2.5': [rem(44), '1.3'],
      'h2.5-m': [rem(34), '1.3'],
      h3: [rem(36), '1.25'],
      'h3-m': [rem(24), '1.4'],
      h4: [rem(28), '1.5'],
      'h4-m': [rem(24), '1.4'],
      // 'h4-m': [rem(20), rem(32)],
      h5: [rem(19), '1.5'],
      'h5-m': [rem(18), rem(32)],
      'body-lg': [rem(22), '1.4'],
      'body-md': [rem(18), '1.5'], // also body-lg mobile
      'body-sm': [rem(16), '1.5'], // also body-md mobile
      'body-xs': [rem(14), rem(20)],
      nav: [
        rem(16),
        {
          lineHeight: rem(20),
          letterSpacing: '-1%',
        },
      ],
      'nav-m': [
        rem(20),
        {
          lineHeight: rem(20),
          letterSpacing: '-1%',
        },
      ],
      20: rem(20),
    },
    lineHeight: {
      none: '1',
      xs: '1.125',
      base: '1.25',
      md: '1.3',
      lg: '1.4',
      xl: '1.5',
    },
    boxShadow: {
      hero: '0 4px 20px rgba(0, 0, 0, 0.05)',
      nav: '0 13px 20px rgba(0, 0, 0, 0.06)',
      default: '0 10px 30px rgba(0, 0, 0, 0.06)',
      hover: '0 3px 6px rgba(0, 0, 0, 0.2)',
      'nav-group': '0px 0px 15px rgba(0, 0, 0, 0.2)',
      'video-card': '0px 13px 20px rgba(0, 0, 0, 0.06)',
      'swiper-nav-btn': '0px 2.5625px 5.125px rgba(0, 0, 0, 0.2)',
    },
    extend: {
      borderWidth: {
        1: '1px',
        3: '3px',
      },
      borderRadius: {
        10: '10px',
      },
      backgroundImage: {
        radial:
          'radial-gradient(50% 50% at 50% 50%, rgba(15, 15, 16, 0.93) 66.15%, rgba(15, 15, 16, 0) 100%)',
        'lms-gradient':
          'linear-gradient(180deg, #000F57 16.99%, #2964F6 103.47%)',
      },
      spacing: {
        'header-height-mobile': '90px',
        'header-height-desktop': '120px',
        15: rem(60),
      },
      padding: ({ theme }) => ({
        menu: `calc(${theme('spacing[header-height-mobile]')} + ${theme(
          'spacing.14'
        )})`,
      }),
      margin: ({ theme }) => ({
        'carousel-offset': `calc(((100vw - ${theme(
          'screens.xl'
        )}) / 2) - 16px + 120px)`, // for larger than 1440
      }),
      bgGradientDeg: {
        109: '109deg',
      },
      animation: {
        'spin-slow': 'spin 3s ease-in-out infinite',
      },
      transitionDuration: {
        0: '0ms',
      },
      letterSpacing: {
        widerer: rem(3),
      },
      maxWidth: ({ theme }) => ({
        desktop: `${theme('screens.xl')}`,
      }),
    },
  },
  variants: {
    extend: {
      backgroundColor: ['hover', 'disabled'],
      opacity: ['hover', 'disabled'],
      boxShadow: ['hover'],
    },
  },
  plugins: [
    // require('@tailwindcss/typography'),
    require('@tailwindcss/aspect-ratio'), // eslint-disable-line global-require
    plugin(function ({ matchUtilities, theme }) {
      matchUtilities(
        {
          'bg-gradient': (angle) => ({
            'background-image': `linear-gradient(${angle}, var(--tw-gradient-stops))`,
          }),
        },
        {
          // values from config and defaults you wish to use most
          values: Object.assign(
            theme('bgGradientDeg', {}), // name of config key. Must be unique
            {
              10: '10deg', // bg-gradient-10
              15: '15deg',
              20: '20deg',
              25: '25deg',
              30: '30deg',
              45: '45deg',
              60: '60deg',
              90: '90deg',
              120: '120deg',
              135: '135deg',
              180: '180deg',
            }
          ),
        }
      );
    }),
  ],
};
